import type { Timestamp } from 'firebase/firestore'

export const REMOTE_HARNESS_SCHEMA_VERSION = 1 as const

export type RemoteHarnessPhase =
  | 'waiting'
  | 'posting_blinds'
  | 'betting'
  | 'awaiting_street'
  | 'awaiting_award'
  | 'awaiting_next_hand'
  | 'paused'

export type RemoteHarnessStreet = 'preflop' | 'flop' | 'turn' | 'river' | 'showdown'
export type RemoteHarnessLegalAction = 'fold' | 'check' | 'call' | 'bet' | 'raise'
export type RemoteBlindKind = 'small' | 'big'

export interface RemoteRequiredBlind {
  playerUid: string
  kind: RemoteBlindKind
  amount: number
}

export interface RemoteHarnessPrompt {
  headline: string
  detail: string
  toCall: number
  legalActions: RemoteHarnessLegalAction[]
}

/**
 * The member-readable projection of a remote heads-up test.
 *
 * This type deliberately has nowhere to put a shuffle seed, an unrevealed board,
 * the deck, or either player's hole cards. Those values must never be written to
 * the public harness document.
 */
export interface RemoteHarnessState {
  schemaVersion: typeof REMOTE_HARNESS_SCHEMA_VERSION
  mode: 'two-human'
  expectedPlayers: 2
  runId: string
  handNumber: number
  phase: RemoteHarnessPhase
  street: RemoteHarnessStreet
  dealerUid: string
  smallBlindUid: string
  bigBlindUid: string
  actorUid: string
  turnToken: string
  canCheck: boolean
  requiredBlind: RemoteRequiredBlind | null
  /** Only cards already revealed to both players. */
  board: string[]
  prompt: RemoteHarnessPrompt
  expectedWinnerUid: string | null
  expectedPot: number
  /** Core game action incorporated into this projection. */
  processedActionId: string
  revision: number
  updatedAt: Timestamp | null
}

/** Stored at games/{code}/harnessHands/{uid}; never collection-read by a player. */
export interface RemoteHoleCards {
  uid: string
  runId: string
  handNumber: number
  cards: [string, string]
  updatedAt: Timestamp | null
}

/**
 * Public representation of the durable core check action. Its Firestore
 * document also contains the standard GameAction audit fields.
 */
export interface RemoteCheckCommand {
  id: string
  type: 'check'
  actorUid: string
  handNumber: number
  turnToken: string
  createdAt: Timestamp | null
}

export type RemoteHarnessStateWrite = Omit<RemoteHarnessState, 'updatedAt'>
export type RemoteHoleCardsWrite = Omit<RemoteHoleCards, 'updatedAt'>
